import React, { useCallback, useEffect, useState } from 'react';

import HistoricoAlteracoes from '../../../components/historico-alteracoes/HistoricoAlteracoes';
import {
  AcaoDeComentarioDeCompra,
  PermissaoDeComentarioDeCompra,
  definirPermissaoDeComentarioDeCompra,
  listarPermissoesDeComentarioDeCompra,
} from '../../../api/comentariosCompraApi';
import { notify } from '../../../shared/feedback/notify';
import '../../../shared/feedback/erro-de-tela.css';
import '../pages/css/adminSistema.css';

/**
 * Quem comenta e quem responde em PEDIDOS e REQUISIÇÕES do Dashboard de Compras (at-129).
 *
 * 🔴 Matriz DENY-BY-DEFAULT (decisão do Victor, 22/10/2026): perfil não marcado não escreve, e com
 * a matriz vazia só o administrador comenta. É o mesmo mecanismo dos comentários do veículo, com
 * duas ações próprias — configuradas aqui, e não na aba da Oficina.
 *
 * ⚠️ Só o perfil GESTAO_COMPRAS é oferecido, de propósito: quem LÊ os comentários é quem alcança o
 * Dashboard de Compras (ADMIN e GESTAO_COMPRAS, at-125). Marcar um perfil que não abre a tela daria uma permissão
 * que ninguém consegue exercer. Quando a leitura se abrir a outro perfil, ele entra nesta lista.
 *
 * Carga própria, como a seção de sincronização ao lado: falha aqui não esconde o resto da tela.
 */
const PAPEIS_OFERECIDOS = ['GESTAO_COMPRAS'];
const ROTULO_DO_PAPEL: Record<string, string> = { GESTAO_COMPRAS: 'Gestão de Compras' };

const ACAO_LABELS: Record<AcaoDeComentarioDeCompra, { titulo: string; descricao: string }> = {
  COMENTAR_COMPRA: {
    titulo: 'Escrever comentários em pedidos e requisições',
    descricao:
      'Registrar uma anotação sobre a compra, no detalhe do pedido ou da requisição. O comentário ' +
      'não pode ser apagado; quem escreveu corrige o texto nos primeiros 10 minutos.',
  },
  RESPONDER_COMPRA: {
    titulo: 'Responder a comentários de compras',
    descricao: 'Responder ao que outra pessoa registrou. É permissão separada de escrever.',
  },
};

const ComentariosCompraPermissoesSection: React.FC = () => {
  const [permissoes, setPermissoes] = useState<PermissaoDeComentarioDeCompra[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [falhou, setFalhou] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [historicoAberto, setHistoricoAberto] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      setPermissoes(await listarPermissoesDeComentarioDeCompra());
      setFalhou(false);
    } catch (error) {
      /*
       * 🔴 Estado seguro + sinal visível (at-60). Aqui o falso vazio é caro: uma matriz que aparece
       * sem perfis por causa de erro convida a "corrigir" e salvar — e o salvamento gravaria o
       * vazio de verdade. Por isso, com a carga falha, as pílulas NÃO são desenhadas.
       */
      setPermissoes([]);
      setFalhou(true);
      notify.errorFrom(
        error,
        'Não foi possível carregar quem comenta em pedidos e requisições. Recarregue a página; se persistir, acione a TI.',
      );
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const alternar = async (permissao: PermissaoDeComentarioDeCompra, papel: string) => {
    if (salvando) return;
    const proximos = permissao.papeis.includes(papel)
      ? permissao.papeis.filter((p) => p !== papel)
      : [...permissao.papeis, papel];
    setSalvando(true);
    try {
      await definirPermissaoDeComentarioDeCompra(permissao.acao, proximos);
      notify.success('Permissão de comentários de compras atualizada.');
      await carregar();
    } catch (error) {
      // Sem sucesso falso: a tela continua mostrando o que está gravado.
      notify.errorFrom(
        error,
        'Não foi possível alterar a permissão de comentários. Tente novamente; se persistir, acione a TI.',
      );
    } finally {
      setSalvando(false);
    }
  };

  return (
    <section className="admin-card">
      <h2 className="admin-card-title">Comentários em pedidos e requisições</h2>
      <p className="admin-hint">
        Quem pode escrever e quem pode responder nos comentários do detalhe de um pedido ou de uma
        requisição. <strong>Perfil não marcado não escreve</strong> — com nada marcado, só os
        administradores comentam. Ler os comentários é de quem abre o Dashboard de Compras.
      </p>

      {falhou ? (
        <p className="erro-de-tela">
          Não foi possível carregar esta configuração. Ela não está vazia: a consulta falhou. Recarregue
          a página antes de alterar qualquer permissão.
        </p>
      ) : carregando ? (
        <p className="admin-hint">Carregando...</p>
      ) : (
        permissoes.map((permissao) => {
          const meta = ACAO_LABELS[permissao.acao] ?? { titulo: permissao.acao, descricao: '' };
          return (
            <div key={permissao.acao} style={{ marginTop: 14 }}>
              <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--tema-texto, #1A1C1B)' }}>{meta.titulo}</div>
              <p className="admin-hint" style={{ margin: '6px 0 10px' }}>
                {meta.descricao}
              </p>
              <div className="admin-role-pills">
                {PAPEIS_OFERECIDOS.map((papel) => {
                  const marcado = permissao.papeis.includes(papel);
                  return (
                    <button
                      key={papel}
                      type="button"
                      disabled={salvando}
                      aria-pressed={marcado}
                      onClick={() => void alternar(permissao, papel)}
                      className={`admin-role-pill ${marcado ? 'active' : ''}`}
                    >
                      {ROTULO_DO_PAPEL[papel] ?? papel}
                    </button>
                  );
                })}
              </div>
              {permissao.papeis.length === 0 && (
                <p className="admin-hint" style={{ marginTop: 8 }}>
                  Nenhum perfil pode fazer isso — só os administradores.
                </p>
              )}
            </div>
          );
        })
      )}

      <button
        type="button"
        className="admin-btn admin-btn-secondary"
        style={{ marginTop: 14 }}
        onClick={() => setHistoricoAberto(true)}
      >
        Ver histórico das permissões de comentário
      </button>

      <HistoricoAlteracoes
        isOpen={historicoAberto}
        onClose={() => setHistoricoAberto(false)}
        entity="comentario_permissao"
        titulo="Histórico das permissões de comentário"
        descricao={
          'Cada mudança fica registrada com quem fez, quando, e quais perfis entraram ou saíram. O ' +
          'histórico reúne as permissões dos comentários de compras e as dos veículos — a ação ' +
          'mostra de qual se trata.'
        }
        rotuloAlvo="Permissão"
      />
    </section>
  );
};

export default ComentariosCompraPermissoesSection;
